import get from 'lodash/get';

import { hasMeaningfulValue, isPlainObject } from '../../../utils';
import type { ComputePaths } from '../types';
import { getCpuPrefix } from './paths';

export function readCpuTotal(formValues: Record<string, unknown>, pathsOrPrefix: ComputePaths | string): number {
  if (typeof pathsOrPrefix === 'string') {
    const cpu = get(formValues, pathsOrPrefix, {}) as Record<string, unknown>;
    const request = Number(cpu.request);
    if (Number.isFinite(request) && request > 0) return request;
    const cards = Number(cpu.physicalCards) || 1;
    const cores = Number(cpu.physicalCores) || 1;
    const logical = Number(cpu.logicalCores) || 1;
    return cards * cores * logical;
  }
  const request = Number(get(formValues, pathsOrPrefix.cpuRequest ?? ''));
  if (pathsOrPrefix.cpuRequest && Number.isFinite(request) && request > 0) return request;
  const cards = Number(get(formValues, pathsOrPrefix.cpuCards)) || 1;
  const cores = Number(get(formValues, pathsOrPrefix.cpuCores)) || 1;
  const logical = Number(get(formValues, pathsOrPrefix.cpuLogical)) || 1;
  return cards * cores * logical;
}

/** 仅从已配置字段读 CPU 总量；空 cpu 对象不 default 为 1（容器 CI fromForm / 计价用） */
export function readConfiguredCpuFromRecord(cpu: unknown): number | undefined {
  if (!cpu || typeof cpu !== 'object' || Array.isArray(cpu)) return undefined;
  const record = cpu as Record<string, unknown>;

  const direct = Number(record.request);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const { physicalCards, physicalCores, logicalCores } = record;
  if (physicalCards == null || physicalCores == null || logicalCores == null) return undefined;
  const cards = Number(physicalCards);
  const cores = Number(physicalCores);
  const logical = Number(logicalCores);
  if (![cards, cores, logical].every((value) => Number.isFinite(value) && value > 0)) return undefined;
  return cards * cores * logical;
}

export function isCpuConfigured(formValues: Record<string, unknown>, paths: ComputePaths): boolean {
  if (paths.cpuRequest && hasMeaningfulValue(get(formValues, paths.cpuRequest))) {
    const request = Number(get(formValues, paths.cpuRequest));
    return Number.isFinite(request) && request > 0;
  }
  const fromLeaves =
    hasMeaningfulValue(get(formValues, paths.cpuCards)) &&
    hasMeaningfulValue(get(formValues, paths.cpuCores)) &&
    hasMeaningfulValue(get(formValues, paths.cpuLogical));
  if (fromLeaves) return true;

  const cpu = get(formValues, getCpuPrefix(paths)) as Record<string, unknown> | undefined;
  if (!cpu || typeof cpu !== 'object') return false;
  if (hasMeaningfulValue(cpu.request)) {
    const request = Number(cpu.request);
    return Number.isFinite(request) && request > 0;
  }
  return hasMeaningfulValue(cpu.physicalCards) && hasMeaningfulValue(cpu.physicalCores) && hasMeaningfulValue(cpu.logicalCores);
}

export function isMemoryConfigured(formValues: Record<string, unknown>, paths: ComputePaths): boolean {
  const memory = get(formValues, paths.memory);
  if (!hasMeaningfulValue(memory)) return false;
  const value = Number(memory);
  return !Number.isNaN(value) && value > 0;
}

/** GPU 以数量为准：无 number 视为未配置 */
export function isGpuConfigured(raw: unknown): boolean {
  if (!isPlainObject(raw)) return false;
  return hasMeaningfulValue(raw.number);
}

export function readGpuFromForm(formValues: Record<string, unknown>, gpuPath: string): Record<string, unknown> | undefined {
  const gpu = get(formValues, gpuPath);
  if (isPlainObject(gpu)) return gpu;
  return undefined;
}
